import { eventChannel } from 'redux-saga';
import { all, put, select, take, takeLatest, delay, race } from 'redux-saga/effects';
import { isNil, unless } from 'ramda';
import { BookingActions, BookingTypes } from './booking.redux';
import { selectBooking, selectBookingPrice, selectBookingStep } from './booking.selectors';
import { BookingStep } from '../../routes/booking/booking.constants';
import { selectCurrentExperienceId, selectCurrentExperienceHomeRoute } from '../experience';
import { AnalyticsActions, GTMEvents, IntercomEvents } from '../analytics';
import { createDatabaseEntry } from '../../shared/services/booking/bookings';
import { selectIsSignedIn, selectUserId, AuthActions } from '../auth';
import { anonymousBookingsRef, bookingsRef, firebaseFunctions } from '../../shared/services/firebase'; // eslint-disable-line
import judoPayment from '../../shared/services/judoPayment';
import { PaymentStatus, JUDO_REQUEST_TIMEOUT } from './booking.constants';
import history from '../../shared/utils/history';
import { selectCurrentKioskName, selectCurrentKioskId, selectIsKioskMode } from '../kiosk';
import reportError from '../../shared/utils/reportError';
import { TRANSITION_TOTAL_DURATION } from '../../shared/components/animated/animated.constants';

const createShortcodeChannel = (bookingKey, isSignedIn) => {
  const bookingRef = isSignedIn ? bookingsRef : anonymousBookingsRef;
  const codeRef = bookingRef.child(bookingKey).child('code');

  return eventChannel(emit => {
    const onCodeValReceived = snapshot => {
      const code = snapshot.val();
      setTimeout(() => unless(isNil, emit)(code));
    };

    codeRef.on('value', onCodeValReceived);

    return () => codeRef.off('value', onCodeValReceived);
  });
};

function* getBookingEntry(payment, { email, firstName, lastName }) {
  const isSignedIn = yield select(selectIsSignedIn);
  const booking = yield select(selectBooking);
  const experienceId = yield select(selectCurrentExperienceId);
  const userId = yield select(selectUserId);
  const kiosk = yield select(selectCurrentKioskName);
  const kioskId = yield select(selectCurrentKioskId);

  return createDatabaseEntry({
    booking,
    experienceId,
    userId: isSignedIn ? userId : email,
    payment,
    kiosk,
    kioskId,
    firstName,
    lastName,
  });
}

function initBooking(isSignedIn) {
  if (isSignedIn) {
    return bookingsRef.push();
  }
  return anonymousBookingsRef.push();
}

function* receiveShortcode(key) {
  const isSignedIn = yield select(selectIsSignedIn);
  const shortcodeChannel = yield createShortcodeChannel(key, isSignedIn);
  const shortcode = yield take(shortcodeChannel);
  shortcodeChannel.close();
  return shortcode;
}

function* saveBookingData(bookingRef, bookingData) {
  yield bookingRef.set(bookingData);
  const code = yield receiveShortcode(bookingRef.key);
  yield put(BookingActions.setCode(code));
  return code;
}

function* createJudoToken() {
  const payment = judoPayment.activeCardDetails;

  const { response, timeout, cancel } = yield race({
    response: judoPayment.createToken(payment),
    timeout: delay(JUDO_REQUEST_TIMEOUT),
    cancel: take(BookingTypes.PAYMENT_TOKEN_RECEIVED),
  });

  if (timeout) {
    throw new Error('Obtaining judo token failed');
  }

  return response ? response.oneUseToken : cancel.token;
}

function* preauthBooking(paymentRef, email) {
  const isSignedIn = yield select(selectIsSignedIn);
  const uid = yield select(selectUserId);
  const price = yield select(selectBookingPrice);
  const token = yield createJudoToken();

  const { data } = yield firebaseFunctions.post('/webPayment/', {
    uid: isSignedIn ? uid : email,
    token,
    amount: price,
    paymentRef,
  });

  return data;
}

function* handlePayment(data) {
  try {
    yield put(BookingActions.setPaymentStatus(PaymentStatus.PENDING));
    const isSignedIn = yield select(selectIsSignedIn);
    const bookingRef = yield initBooking(isSignedIn);

    const paymentDetails = yield preauthBooking(bookingRef.key, data.email);
    const bookingData = yield getBookingEntry(paymentDetails, data);

    yield put(BookingActions.setPaymentStatus(PaymentStatus.SUCCESSFUL));
    yield put(BookingActions.nextStep());

    const id = yield saveBookingData(bookingRef, bookingData);

    yield put(AnalyticsActions.logGTMEvent(GTMEvents.Purchase, { id, bookingData }));
    yield put(AnalyticsActions.logIntercomEvent(IntercomEvents.PurchaseComplete));
  } catch (e) {
    yield put(BookingActions.setPaymentStatus(PaymentStatus.FAILED));
    reportError(e);
  }
}

function* handleNextStepRequested(data) {
  const currentStep = yield select(selectBookingStep);
  if (currentStep === BookingStep.PAYMENT) {
    yield handlePayment(data.data);
  } else if (currentStep === BookingStep.TIME) {
    yield put(AnalyticsActions.logGTMEvent(GTMEvents.CheckoutStep1));
    yield put(BookingActions.nextStep());
  } else {
    yield put(BookingActions.nextStep());
  }
}

function* handleStepChanged() {
  const currentStep = yield select(selectBookingStep);

  if (currentStep === BookingStep.PARTICIPANTS) {
    yield put(AnalyticsActions.logGTMEvent(GTMEvents.CheckoutStep3Init));
    yield put(AnalyticsActions.logIntercomEvent(IntercomEvents.CheckoutGuests));
  }

  if (currentStep === BookingStep.PAYMENT) {
    yield put(AnalyticsActions.logGTMEvent(GTMEvents.CheckoutStep3));
    yield put(AnalyticsActions.logGTMEvent(GTMEvents.CheckoutStep4Init));
    yield put(AnalyticsActions.logIntercomEvent(IntercomEvents.CheckoutPayment));
    yield put(BookingActions.setPaymentStatus(PaymentStatus.INITIAL));
  }
}

function* resetBooking() {
  const isSignedIn = yield select(selectIsSignedIn);
  const isKioskMode = yield select(selectIsKioskMode);
  if (isSignedIn && isKioskMode) {
    yield put(AuthActions.signOut());
  }
  document.body.dispatchEvent(new Event('locationChange'));
}

function* handleCloseBooking() {
  yield resetBooking();
  const currentExperienceUrl = yield select(selectCurrentExperienceHomeRoute);
  history.push(currentExperienceUrl);
}

function* handleClearBooking() {
  yield put(AuthActions.hideModal());
  yield resetBooking();
}

function* handleStartBooking() {
  yield delay(TRANSITION_TOTAL_DURATION);
  yield put(AnalyticsActions.logGTMEvent(GTMEvents.CheckoutStep1Init));
  yield put(AnalyticsActions.logIntercomEvent(IntercomEvents.CheckoutDatesAndTimes));
}

export default function* bookingSaga() {
  yield all([
    takeLatest(BookingTypes.REQUEST_NEXT_STEP, handleNextStepRequested),
    takeLatest(BookingTypes.NEXT_STEP, handleStepChanged),
    takeLatest(BookingTypes.CLOSE_BOOKING, handleCloseBooking),
    takeLatest(BookingTypes.CLEAR_BOOKING, handleClearBooking),
    takeLatest(BookingTypes.START_BOOKING, handleStartBooking),
  ]);
}
